/**
 * Utility Functions for the Web App
 *
 * This file contains utility functions specific to the frontend.
 * It includes UI helpers, class name utilities, and client-side
 * functionality that doesn't belong in the shared package.
 */

import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

// =============================================================================
// CLASS NAME UTILITIES
// =============================================================================

/**
 * Combine and merge Tailwind CSS classes efficiently
 * This prevents conflicts and optimizes the final class string
 *
 * Example: cn('bg-red-500', 'bg-blue-500') -> 'bg-blue-500'
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Generate conditional class names based on state
 * Useful for styling components based on props or state
 */
export function conditionalClass(
  baseClass: string,
  condition: boolean,
  trueClass: string,
  falseClass?: string
): string {
  return cn(baseClass, condition ? trueClass : falseClass);
}

// =============================================================================
// UI STATE UTILITIES
// =============================================================================

/**
 * Get the appropriate color class for confidence levels
 * Used throughout the app to show AI confidence visually
 */
export function getConfidenceColorClass(confidence: number): string {
  if (confidence >= 0.8) {
    return 'text-success-600 bg-success-50 border-success-200';
  }
  if (confidence >= 0.5) {
    return 'text-warning-600 bg-warning-50 border-warning-200';
  }
  return 'text-error-600 bg-error-50 border-error-200';
}

/**
 * Get the appropriate color class for operation status
 * Used in status badges and indicators
 */
export function getStatusColorClass(status: string): string {
  switch (status) {
    case 'completed':
      return 'text-success-600 bg-success-50 border-success-200';
    case 'processing':
    case 'in_progress':
      return 'text-primary-600 bg-primary-50 border-primary-200';
    case 'failed':
    case 'error':
      return 'text-error-600 bg-error-50 border-error-200';
    case 'cancelled':
      return 'text-[var(--text-secondary)] bg-[var(--surface-hover)] border-[var(--border-default)]';
    case 'pending':
    default:
      return 'text-warning-600 bg-warning-50 border-warning-200';
  }
}

/**
 * Get an appropriate icon for operation status
 * Returns the name of a Lucide icon to display
 */
export function getStatusIcon(status: string): string {
  switch (status) {
    case 'completed':
      return 'CheckCircle';
    case 'processing':
    case 'in_progress':
      return 'Loader';
    case 'failed':
    case 'error':
      return 'XCircle';
    case 'cancelled':
      return 'X';
    case 'pending':
    default:
      return 'Clock';
  }
}

// =============================================================================
// FORM UTILITIES
// =============================================================================

/**
 * Extract form data into a plain object
 * Handles FormData conversion with proper typing
 */
export function extractFormData<T extends Record<string, unknown>>(
  formData: FormData
): T {
  const data: Record<string, unknown> = {};

  for (const [key, value] of Array.from(formData.entries())) {
    // Handle multiple values for the same key (checkboxes, multi-selects)
    if (data[key]) {
      // Convert to array if not already
      if (!Array.isArray(data[key])) {
        data[key] = [data[key]];
      }
      (data[key] as unknown[]).push(value);
    } else {
      data[key] = value;
    }
  }

  return data as T;
}

/**
 * Validate required form fields
 * Returns an object with validation errors
 */
export function validateRequiredFields(
  data: Record<string, unknown>,
  requiredFields: string[]
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const field of requiredFields) {
    const value = data[field];
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      errors[field] = `${field} is required`;
    }
  }

  return errors;
}

// =============================================================================
// FILE UTILITIES
// =============================================================================

/**
 * Check if a file is a valid CSV or Excel file
 * Used for upload validation
 */
export function isValidImportFile(file: File): boolean {
  const validMimeTypes = [
    'text/csv',
    'application/csv',
    'text/plain',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ];

  const validExtensions = ['.csv', '.txt', '.xls', '.xlsx'];

  const hasValidMimeType = validMimeTypes.includes(file.type);
  const hasValidExtension = validExtensions.some(ext =>
    file.name.toLowerCase().endsWith(ext)
  );

  return hasValidMimeType || hasValidExtension;
}

/**
 * Get a human-readable description of file type
 */
export function getFileTypeDescription(file: File): string {
  if (file.name.toLowerCase().endsWith('.csv')) {
    return 'CSV file';
  }
  if (file.name.toLowerCase().endsWith('.xlsx')) {
    return 'Excel file (XLSX)';
  }
  if (file.name.toLowerCase().endsWith('.xls')) {
    return 'Excel file (XLS)';
  }
  if (file.type === 'text/plain') {
    return 'Text file';
  }
  return 'Unknown file type';
}

/**
 * Read a file as text
 * Returns a promise that resolves with the file content
 */
export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const result = event.target?.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('Failed to read file as text'));
      }
    };

    reader.onerror = () => {
      reject(new Error('Error reading file'));
    };

    reader.readAsText(file);
  });
}

// =============================================================================
// LOCAL STORAGE UTILITIES
// =============================================================================

/**
 * Safely get an item from localStorage
 * Returns null if the item doesn't exist or localStorage is unavailable
 */
export function getLocalStorageItem(key: string): string | null {
  if (typeof window === 'undefined') {
    return null; // Server-side rendering
  }

  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn('Failed to read from localStorage:', error);
    return null;
  }
}

/**
 * Safely set an item in localStorage
 * Returns true if successful, false otherwise
 */
export function setLocalStorageItem(key: string, value: string): boolean {
  if (typeof window === 'undefined') {
    return false; // Server-side rendering
  }

  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.warn('Failed to write to localStorage:', error);
    return false;
  }
}

/**
 * Safely remove an item from localStorage
 */
export function removeLocalStorageItem(key: string): boolean {
  if (typeof window === 'undefined') {
    return false; // Server-side rendering
  }

  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn('Failed to remove from localStorage:', error);
    return false;
  }
}

/**
 * Get and parse a JSON object from localStorage
 * Returns the parsed object or null if parsing fails
 */
export function getLocalStorageJSON<T>(key: string): T | null {
  const item = getLocalStorageItem(key);
  if (!item) return null;

  try {
    return JSON.parse(item) as T;
  } catch (error) {
    console.warn('Failed to parse JSON from localStorage:', error);
    return null;
  }
}

/**
 * Stringify and save a JSON object to localStorage
 */
export function setLocalStorageJSON<T>(key: string, value: T): boolean {
  try {
    const jsonString = JSON.stringify(value);
    return setLocalStorageItem(key, jsonString);
  } catch (error) {
    console.warn('Failed to stringify JSON for localStorage:', error);
    return false;
  }
}

// =============================================================================
// URL UTILITIES
// =============================================================================

/**
 * Create a URL with query parameters
 * Handles encoding and null/undefined values
 */
export function createUrlWithParams(
  baseUrl: string,
  params: Record<string, string | number | boolean | null | undefined>
): string {
  const url = new URL(baseUrl, window.location.origin);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      url.searchParams.set(key, String(value));
    }
  });

  return url.toString();
}

/**
 * Get query parameters from the current URL
 * Returns an object with all query parameters
 */
export function getQueryParams(): Record<string, string> {
  if (typeof window === 'undefined') {
    return {}; // Server-side rendering
  }

  const params: Record<string, string> = {};
  const searchParams = new URLSearchParams(window.location.search);

  searchParams.forEach((value, key) => {
    params[key] = value;
  });

  return params;
}

// =============================================================================
// DEBOUNCE AND THROTTLE
// =============================================================================

/**
 * Debounce a function call
 * Useful for search inputs and API calls
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Throttle a function call
 * Useful for scroll events and frequent updates
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

// =============================================================================
// COPY TO CLIPBOARD
// =============================================================================

/**
 * Copy text to clipboard
 * Uses the modern Clipboard API with fallback
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof window === 'undefined') {
    return false; // Server-side rendering
  }

  try {
    // Use modern Clipboard API if available
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const success = document.execCommand('copy');
    textArea.remove();
    return success;
  } catch (error) {
    console.warn('Failed to copy to clipboard:', error);
    return false;
  }
}

// =============================================================================
// KEYBOARD UTILITIES
// =============================================================================

/**
 * Check if a keyboard event matches a key combination
 * Useful for keyboard shortcuts
 */
export function isKeyboardEvent(
  event: KeyboardEvent,
  key: string,
  modifiers: {
    ctrl?: boolean;
    shift?: boolean;
    alt?: boolean;
    meta?: boolean;
  } = {}
): boolean {
  const keyMatches = event.key.toLowerCase() === key.toLowerCase();
  const ctrlMatches = !!modifiers.ctrl === event.ctrlKey;
  const shiftMatches = !!modifiers.shift === event.shiftKey;
  const altMatches = !!modifiers.alt === event.altKey;
  const metaMatches = !!modifiers.meta === event.metaKey;

  return keyMatches && ctrlMatches && shiftMatches && altMatches && metaMatches;
}

/**
 * Get a human-readable keyboard shortcut description
 */
export function getKeyboardShortcutText(
  key: string,
  modifiers: {
    ctrl?: boolean;
    shift?: boolean;
    alt?: boolean;
    meta?: boolean;
  } = {}
): string {
  const parts: string[] = [];

  if (modifiers.meta) parts.push('⌘');
  if (modifiers.ctrl) parts.push('Ctrl');
  if (modifiers.alt) parts.push('Alt');
  if (modifiers.shift) parts.push('Shift');
  parts.push(key.toUpperCase());

  return parts.join(' + ');
}